import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Badge } from "reactstrap";
import useLeads from "../../Hooks/useLeads";

const LeadActivityModal = ({ isOpen, toggle, source, leadId, leadName }) => {
  const [page, setPage] = useState(1);
  const { activities, activityPagination, activityLoading, fetchLeadActivity } = useLeads();

  useEffect(() => {
    if (isOpen && leadId && source) {
      fetchLeadActivity(source, leadId, { page, limit: 10 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, leadId, source, page]);

  const getActionBadge = (action, field) => {
    if (action === "call_initiated") return <Badge color="success"><i className="mdi mdi-phone me-1"/>Call Initiated</Badge>;
    if (action === "whatsapp_initiated") return <Badge color="info"><i className="mdi mdi-whatsapp me-1"/>WhatsApp Initiated</Badge>;
    if (action === "stage_updated" || field === "stage") return <Badge color="primary"><i className="mdi mdi-stagewise me-1"/>Stage Updated</Badge>;
    if (action === "remark_updated" || field === "remark") return <Badge color="warning" className="text-dark"><i className="mdi mdi-comment-text me-1"/>Remark Updated</Badge>;
    if (action === "lead_viewed" || field === "lead_view") return <Badge color="secondary"><i className="mdi mdi-eye me-1"/>Lead Viewed</Badge>;
    return <Badge color="dark">{action || field || "Activity"}</Badge>;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle} className="bg-light">
        <div className="d-flex align-items-center gap-2">
          <i className="mdi mdi-history text-primary fs-4" />
          <div>
            <h5 className="modal-title mb-0">Activity & Manager History Log</h5>
            <small className="text-muted">Lead: <span className="fw-bold">{leadName || "Selected Lead"}</span></small>
          </div>
        </div>
      </ModalHeader>

      <ModalBody style={{ maxHeight: "65vh", overflowY: "auto" }}>
        {activityLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <div className="mt-2 text-muted small">Loading activity logs...</div>
          </div>
        ) : activities && activities.length > 0 ? (
          <div className="activity-timeline px-2">
            {activities.map((act) => (
              <div key={act._id} className="card border mb-3 shadow-sm">
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <div>
                      {getActionBadge(act.action, act.field)}
                      <span className="ms-2 small text-muted">
                        by <strong className="text-dark">{act.changedByName || "System"}</strong>
                      </span>
                    </div>
                    <small className="text-muted fw-semibold" style={{ fontSize: "11px" }}>
                      <i className="mdi mdi-clock-outline me-1" />
                      {formatTime(act.createdAt)}
                    </small>
                  </div>

                  {act.field === "stage" && (
                    <div className="small mt-1 p-2 bg-light rounded border-start border-3 border-primary">
                      <span className="text-muted">Stage changed: </span>
                      <span className="badge bg-secondary-subtle text-secondary me-1">{act.oldValueDisplay || "Previous"}</span>
                      <i className="mdi mdi-arrow-right me-1 text-primary" />
                      <span className="badge bg-primary">{act.newValueDisplay || "New Stage"}</span>
                    </div>
                  )}

                  {act.field === "remark" && (
                    <div className="small mt-1 p-2 bg-light rounded border-start border-3 border-warning">
                      <span className="text-muted">Remark: </span>
                      <span className="fw-semibold text-dark">{act.newValueDisplay || act.newValue || "-"}</span>
                    </div>
                  )}

                  {act.comment && (
                    <div className="small text-muted mt-2 fst-italic">
                      <i className="mdi mdi-message-text-outline me-1" />
                      "{act.comment}"
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-5 text-muted">
            <i className="mdi mdi-text-box-remove-outline fs-1 opacity-50 d-block mb-2" />
            No activity history logged yet for this lead.
          </div>
        )}
      </ModalBody>

      <ModalFooter className="d-flex justify-content-between align-items-center">
        <div className="small text-muted">
          Page {activityPagination?.page || 1} of {activityPagination?.totalPages || 1} ({activityPagination?.total || 0} total logs)
        </div>
        <div className="d-flex gap-2">
          <Button
            size="sm"
            color="light"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
          >
            Previous
          </Button>
          <Button
            size="sm"
            color="light"
            disabled={page >= (activityPagination?.totalPages || 1)}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
          <Button color="secondary" size="sm" onClick={toggle}>
            Close
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default LeadActivityModal;
